import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CompanyAutocomplete from './CompanyAutocomplete';
import { API_URL } from '../services/api';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PRICE_WITH_EMAIL, PRICE_NO_EMAIL } from '../config/pricing';

const BusinessDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    email: '',
    subject: '',
    message: '',
    privacyAccepted: false
  });
  const [downloadCode, setDownloadCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [type, setType] = useState('existing');
  const [emailFilter, setEmailFilter] = useState('all');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [displayedPreview, setDisplayedPreview] = useState(null);

  // Add useEffect to log state changes
  useEffect(() => {
    console.log('State Update:', { filtersChanged, hasSearched });
  }, [filtersChanged, hasSearched]);

  const handleCompanySelect = (company) => {
    setFiltersChanged(true);
    setHasSearched(false);
    console.log('Company filter changed:', { filtersChanged: true, hasSearched: false });
    
    // Clear previous search results
    setDisplayedPreview(null);

    if (!company) {
      setSelectedCompanies([]);
      setType('existing');
      return;
    }

    if (company.type === 'startup') {
      setSelectedCompanies([]);
      setType('startup');
    } else {
      setType('existing');
      if (!selectedCompanies.find(c => c?.id === company?.id)) {
        setSelectedCompanies([...selectedCompanies, company]);
      }
    }

    console.log('Company selection updated:', {
      companyType: company?.type,
      searchType: type,
      selectedCompanies: selectedCompanies
    });
  };

  const handleRemoveCompany = (companyId) => {
    if (!companyId) return;
    
    // Clear previous search results
    setDisplayedPreview(null);
    
    // Reset search states
    setFiltersChanged(true);
    setHasSearched(false);
    
    // Remove the company
    setSelectedCompanies(selectedCompanies.filter(c => c?.id !== companyId));
    
    console.log('Company removed:', { 
      removedId: companyId, 
      filtersChanged: true, 
      hasSearched: false 
    });
  };

  const fetchEntryCount = async () => {
    console.log('Fetch starting with:', { type, selectedCompanies });
    
    if (!startDate || !endDate) {
      setError('Please select a date range');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFiltersChanged(false);
      setHasSearched(true);
      

      const queryParams = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        type: type,  // This will be either 'startup' or 'existing'
      });

      // Only append companies for 'existing' type searches
      if (type === 'existing' && selectedCompanies.length > 0) {
        const companyNames = selectedCompanies.map(c => c.name);
        queryParams.append('companies', companyNames.join(','));
      }
      // For 'startup' type, we don't need to append companies as the backend
      // will filter by company type instead of specific companies

      console.log('Query params:', queryParams.toString());

      const response = await fetch(`${API_URL}/api/data/count?${queryParams}`);
      if (!response.ok) {
        throw new Error('Failed to fetch count');
      }

      const data = await response.json();
      console.log('Response data:', data);
      
      const newPreview = {
        count: data.count,
        withEmail: data.withEmail,
        withoutEmail: data.withoutEmail,
        totalPrice: parseFloat(data.estimatedPrice)
      };

      setDisplayedPreview(newPreview);

    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!displayedPreview?.count) {
      setError('Please check available entries first');
      return;
    }

    try {
      const amount = emailFilter === 'all' 
        ? ((displayedPreview.withEmail * PRICE_WITH_EMAIL) + (displayedPreview.withoutEmail * PRICE_NO_EMAIL)).toFixed(2)
        : emailFilter === 'with'
        ? (displayedPreview.withEmail * PRICE_WITH_EMAIL).toFixed(2)
        : (displayedPreview.withoutEmail * PRICE_NO_EMAIL).toFixed(2);

      const response = await fetch(`${API_URL}/api/stripe/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          searchParameters: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            type: type,
            companies: selectedCompanies,
            withEmail: displayedPreview.withEmail,
            withoutEmail: displayedPreview.withoutEmail,
            emailFilter: emailFilter,
            contactEmail: contactForm.email
          },
          amount: parseFloat(amount)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { id: sessionId } = await response.json();
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setError(error.message);
    }
  };

  const handleDownload = async () => {
    if (!downloadCode) {
      setError('Please enter a download code');
      return;
    }

    // Add validation before sending
    if (!/^[0-9a-f]{12}$/.test(downloadCode)) {
      setError('Invalid code format. Please enter a 12-character hex code.');
      return;
    }

    try {
      setLoading(true);
      console.log('🚀 Starting download with code:', downloadCode);

      const response = await fetch(`${API_URL}/api/data/download/${downloadCode}`);
      console.log('📥 Response received:', {
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type')
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Download error response:', errorData);
        
        if (errorData.code === 'EXCEEDS_PAID_LIMIT') {
          setError(`Cannot download more entries than purchased. You paid for ${errorData.paid.withEmail} entries with email and ${errorData.paid.withoutEmail} without email.`);
        } else {
          setError(errorData.message || 'Invalid or expired download code');
        }
        
        setLoading(false);
        return;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        console.error('❌ JSON error response:', errorData);
        setLoading(false);
        setError(errorData.message || 'Download failed');
        return; // Add early return
      }

      console.log('✅ Starting blob download');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'feedback-data.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('✅ Download completed');
      setSuccess('Download successful!');
      setLoading(false); // Explicitly set loading to false on success
    } catch (error) {
      console.error('❌ Download error:', error);
      setError(error.message || 'Download failed');
      setLoading(false); // Explicitly set loading to false on error
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.privacyAccepted) {
      setError('Please accept the privacy policy');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      setSuccess('Message sent successfully');
      setShowContactForm(false);
      setContactForm({
        email: '',
        subject: '',
        message: '',
        privacyAccepted: false
      });
    } catch (error) {
      setError('Failed to send message: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailFilterChange = (e, newFilter) => {
    if (newFilter !== null) {
      setEmailFilter(newFilter);
    }
  };

  // Common card styling
  const darkCardStyle = {
    bgcolor: 'rgba(20, 20, 20, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRadius: '24px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    outline: '1px solid rgba(62,174,255,0.1)',
  };

  const whiteTextFieldStyle = {
    mb: 2,
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiInputBase-input': { color: 'white' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
      '&.Mui-focused fieldset': { borderColor: '#3EAEFF' }
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#0A0A0A',
      color: 'white',
      py: 8
    }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, position: 'relative' }}>
        {/* Back Button */}
        <IconButton
          component={Link}
          to="/"
          sx={{
            position: 'absolute',
            top: -40,
            left: 24,
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            '&:hover': {
              borderColor: '#3EAEFF',
              backgroundColor: 'rgba(62,174,255,0.1)'
            }
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        {/* Header */}
        <Typography 
          variant="h1" 
          align="center" 
          sx={{
            fontSize: { xs: '2.5rem', md: '4.5rem' },
            fontWeight: 800,
            background: 'linear-gradient(45deg, #3EAEFF 0%, #FF6347 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            textShadow: '0 0 40px rgba(62,174,255,0.3)'
          }}
        >
          Access User Feedback Data
        </Typography>
        <Typography 
          variant="h2" 
          align="center"
          sx={{
            fontSize: { xs: '1.2rem', md: '1.5rem' },
            color: 'rgba(255,255,255,0.8)',
            mb: 6
          }}
        >
          Get valuable insights from real users
        </Typography>

        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          width: '100%',
        }}>
          {/* Left Column - Search Data Card */}
          <Box sx={{ 
            flex: { xs: '1', md: '1.5' },
            maxWidth: '600px',
          }}>
            <Card sx={{ 
              bgcolor: 'rgba(20, 20, 20, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              outline: '1px solid rgba(62,174,255,0.1)',
            }}>
              <CardContent>
                <Typography 
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    fontWeight: 800,
                    textAlign: 'center',
                    background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                  }}
                >
                  Search Data
                </Typography>
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1.2rem',
                    textAlign: 'left',  // Left align
                    alignSelf: 'flex-start'  // Push to left
                  }}
                >
                  Select Data Range
                </Typography>
                <Box sx={{ mt: 4, mb: 4 }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '1.2rem',
                      mb: 2
                    }}
                  >
                    Date Range
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'center',
                    gap: { xs: 3, sm: 3 },    // Increased gap on mobile
                    width: '100%',
                    maxWidth: '500px',
                    mb: 3,
                    '& .react-datepicker-wrapper': {
                      width: { xs: '85%', md: '100%' }
                    },
                    '& .date-picker': {
                      width: '100%',
                      height: { xs: '48px', sm: 'auto' },  // Add fixed height on mobile
                      padding: { xs: '12px', sm: '8px 12px' },  // Increased padding on mobile
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '4px',
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '0.875rem',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#3EAEFF',
                        backgroundColor: 'rgba(62,174,255,0.1)'
                      },
                      '&:focus': {
                        outline: 'none',
                        borderColor: '#3EAEFF',
                        backgroundColor: 'rgba(62,174,255,0.1)'
                      }
                    }
                  }}>
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => {
                        setStartDate(date);
                        setFiltersChanged(true);
                        setHasSearched(false);
                        console.log('Start date changed:', { 
                          date, 
                          filtersChanged: true, 
                          hasSearched: false 
                        });
                      }}
                      selectsStart
                      startDate={startDate}
                      endDate={endDate}
                      maxDate={new Date()}
                      placeholderText="Start Date"
                      className="date-picker"
                      dateFormat="MMM d, yyyy"
                    />
                    <DatePicker
                      selected={endDate}
                      onChange={(date) => {
                        setEndDate(date);
                        setFiltersChanged(true);
                        setHasSearched(false);
                        setDisplayedPreview(null);
                        console.log('End date changed:', { 
                          date, 
                          filtersChanged: true, 
                          hasSearched: false 
                        });
                      }}
                      selectsEnd
                      startDate={startDate}
                      endDate={endDate}
                      minDate={startDate}
                      maxDate={new Date()}
                      placeholderText="End Date"
                      className="date-picker"
                      dateFormat="MMM d, yyyy"
                    />
                  </Box>
                </Box>

                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ 
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '1.2rem',
                    textAlign: 'left',  // Left align
                    alignSelf: 'flex-start'  // Push to left
                  }}
                >
                  Select Companies
                </Typography>
                <Box sx={{ 
                  mb: 3,
                  width: '100%',  // Added full width
                  '& .MuiAutocomplete-root': {
                    minHeight: { xs: '64px', md: '56px' },
                    width: '100%',  // Added full width
                    '& .MuiInputLabel-root': {  // Add this to center the label
                      textAlign: 'center',
                      width: '100%',
                      left: 0,
                      transform: 'translate(0, 16px) scale(1)'
                    }
                  },
                  '& .MuiInputBase-root': {
                    minHeight: { xs: '64px', md: '56px' },
                    fontSize: { xs: '1.2rem', md: '1rem' },
                    padding: { xs: '12px', md: '13px' }
                  },
                  '& .MuiOutlinedInput-root': {
                    width: '100%',  // Added full width
                    padding: { xs: '12px 16px', md: '9px 14px' }
                  },
                  '& .MuiAutocomplete-input': {
                    padding: { xs: '4px 8px', md: '7.5px 14px' },
                    height: { xs: '40px', md: 'auto' },
                    fontSize: { xs: '1.2rem', md: '1rem' }
                  }
                }}>
                  <CompanyAutocomplete
                    onCompanySelect={handleCompanySelect}
                    darkMode={true}
                    isBusinessDashboard={true}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {type === 'existing' && selectedCompanies.map(company => (
                    <Chip
                      key={company.id}
                      label={company.name}
                      onDelete={() => handleRemoveCompany(company.id)}
                      sx={{
                        bgcolor: 'rgba(62,174,255,0.2)',
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.7)',
                          '&:hover': { color: 'white' }
                        }
                      }}
                    />
                  ))}
                </Box>

                <Box sx={{ mt: 4, mb: 4 }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '1.2rem',
                      textAlign: 'left',  // Left align
                      alignSelf: 'flex-start'  // Push to left
                    }}
                  >
                    Email Filter
                  </Typography>
                  <ToggleButtonGroup
                    value={emailFilter}
                    exclusive
                    onChange={handleEmailFilterChange}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      '& .MuiToggleButton-root': {
                        flex: { xs: 1, sm: 'none' },  // Full width on mobile, auto on desktop
                        color: 'rgba(255,255,255,0.7)',
                        borderColor: 'rgba(255,255,255,0.2)',
                        '&.Mui-selected': {
                          color: '#3EAEFF',
                          backgroundColor: 'rgba(62,174,255,0.1)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        }
                      }
                    }}
                  >
                    <ToggleButton value="all">
                      All Entries
                    </ToggleButton>
                    <ToggleButton value="with">
                      With Email
                    </ToggleButton>
                    <ToggleButton value="without">
                      No Email
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Button
                  variant="contained"
                  onClick={fetchEntryCount}
                  disabled={loading || !startDate || !endDate || (hasSearched && !filtersChanged)}
                  sx={{
                    background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
                    },
                    opacity: (hasSearched && !filtersChanged) ? 0.5 : 1,
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Check Available Entries'}
                </Button>
              </CardContent>
            </Card>
          </Box>

          {/* Right Column */}
          <Box sx={{ 
            flex: { xs: '1', md: '1' },
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
            {/* Move Purchase Preview Card here - keeping all original content and logic */}
            {displayedPreview && (
              <Card sx={{ 
                bgcolor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                outline: '1px solid rgba(62,174,255,0.1)',
              }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: '#3EAEFF', mb: 2 }}>
                    Found {emailFilter === 'all' ? displayedPreview.count :
                           emailFilter === 'with' ? displayedPreview.withEmail :
                           displayedPreview.withoutEmail} matching entries
                  </Typography>
                  
                  <Box sx={{ pl: 2, mb: 3 }}>
                    {emailFilter === 'all' ? (
                      <>
                        <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                          • {displayedPreview.withEmail} entries with email
                          <Box component="span" sx={{ color: '#3EAEFF' }}>
                            {' '}(${PRICE_WITH_EMAIL} each = ${(displayedPreview.withEmail * PRICE_WITH_EMAIL).toFixed(2)})
                          </Box>
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                          • {displayedPreview.withoutEmail} entries without email
                          <Box component="span" sx={{ color: '#3EAEFF' }}>
                            {' '}(${PRICE_NO_EMAIL} each = ${(displayedPreview.withoutEmail * PRICE_NO_EMAIL).toFixed(2)})
                          </Box>
                        </Typography>
                      </>
                    ) : emailFilter === 'with' ? (
                      <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                        Found {displayedPreview.withEmail} entries with email
                        <Box component="span" sx={{ color: '#3EAEFF' }}>
                          {' '}(${PRICE_WITH_EMAIL} each = ${(displayedPreview.withEmail * PRICE_WITH_EMAIL).toFixed(2)})
                        </Box>
                      </Typography>
                    ) : (
                      <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                        Found {displayedPreview.withoutEmail} entries without email
                        <Box component="span" sx={{ color: '#3EAEFF' }}>
                          {' '}(${PRICE_NO_EMAIL} each = ${(displayedPreview.withoutEmail * PRICE_NO_EMAIL).toFixed(2)})
                        </Box>
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ 
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    pt: 2,
                    mt: 2  // Add margin top
                  }}>
                    <Typography variant="h6" sx={{ color: '#3EAEFF', mb: 2 }}>  {/* Add margin bottom */}
                      Total Price: ${emailFilter === 'with' 
                        ? (displayedPreview.withEmail * PRICE_WITH_EMAIL).toFixed(2)
                        : emailFilter === 'without'
                        ? (displayedPreview.withoutEmail * PRICE_NO_EMAIL).toFixed(2)
                        : ((displayedPreview.withEmail * PRICE_WITH_EMAIL) + (displayedPreview.withoutEmail * PRICE_NO_EMAIL)).toFixed(2)}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      onClick={handlePurchase}
                      disabled={loading}
                      fullWidth
                      sx={{
                        background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
                        py: 2,
                        fontSize: '1.2rem',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
                        }
                      }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Purchase Data'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Download Card */}
            <Card sx={darkCardStyle}>
              <CardContent>
                <Typography 
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    fontWeight: 800,
                    textAlign: 'center',
                    background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                  }}
                >
                  Download Purchased Data
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },  // Stack on mobile
                  gap: 2,
                  '& .MuiTextField-root': {
                    minHeight: '48px',  // Reduced height
                  },
                  '& .MuiInput-root': {
                    minHeight: '48px',  // Reduced height
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    padding: { xs: '6px', md: '10px' }  // Reduced padding
                  },
                  '& .MuiInput-input': {
                    minHeight: '48px',  // Reduced height
                    fontSize: { xs: '1.1rem', md: '1rem' }
                  }
                }}>
                  <TextField
                    value={downloadCode}
                    onChange={(e) => {
                      // Allow hex characters (0-9, a-f) and limit to 12 characters
                      const value = e.target.value.replace(/[^0-9a-f]/gi, '').slice(0, 12);
                      setDownloadCode(value.toLowerCase());
                    }}
                    placeholder="Enter 12-character code"
                    inputProps={{
                      maxLength: 12,
                      pattern: '[0-9a-f]*',
                    }}
                    fullWidth
                    variant="standard"
                    sx={{ 
                      '& .MuiInput-root': {
                        color: 'white',
                        fontSize: '2rem',
                        letterSpacing: '0.5rem',  // Add spacing between characters
                        '&:before': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&:after': { borderColor: '#3EAEFF' },
                        '&:hover:before': { borderColor: 'rgba(255,255,255,0.4)' },
                      },
                      '& .MuiInput-input': {
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:focus': {
                          transform: 'scale(1.02)',
                        },
                        '&::placeholder': {
                          color: 'rgba(255,255,255,0.5)',
                          opacity: 1,
                          fontSize: '1.5rem',  // Slightly smaller placeholder
                          letterSpacing: 'normal',  // Reset letter spacing for placeholder
                          textAlign: 'center',
                          transition: 'all 0.3s ease'
                        }
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={!downloadCode || loading}
                    sx={{
                      background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Download'}
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Help Card */}
            <Card sx={darkCardStyle}>
              <CardContent>
                <Typography 
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    fontWeight: 800,
                    textAlign: 'center',
                    background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 4,
                  }}
                >
                  Need Help?
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setShowContactForm(true)}
                  sx={{
                    width: '100%',
                    py: 1.5,
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    '&:hover': {
                      borderColor: '#3EAEFF',
                      backgroundColor: 'rgba(62,174,255,0.1)'
                    }
                  }}
                >
                  Contact Support
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Error/Success Messages */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!success} 
          autoHideDuration={6000} 
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Snackbar>

        {/* Contact Form Dialog */}
        <Dialog 
          open={showContactForm} 
          onClose={() => setShowContactForm(false)}
          PaperProps={{
            sx: { 
              bgcolor: '#1a1a1a',
              color: 'white',
              backgroundImage: 'linear-gradient(135deg, rgba(62,174,255,0.1) 0%, rgba(255,99,71,0.1) 100%)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              '& .MuiDialogTitle-root': {
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }
            }
          }}
        >
          <DialogTitle>Contact Support</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleContactSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
                sx={whiteTextFieldStyle}
              />
              <TextField
                fullWidth
                label="Subject"
                value={contactForm.subject}
                onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                required
                sx={whiteTextFieldStyle}
              />
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={4}
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                required
                sx={whiteTextFieldStyle}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contactForm.privacyAccepted}
                    onChange={(e) => setContactForm({ ...contactForm, privacyAccepted: e.target.checked })}
                    sx={{ color: '#3EAEFF' }}
                  />
                }
                label="I agree to the privacy policy"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowContactForm(false)} sx={{ color: 'white' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleContactSubmit}
              disabled={!contactForm.privacyAccepted || loading}
              sx={{
                background: 'linear-gradient(45deg, #3EAEFF 30%, #FF6347 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FF6347 30%, #3EAEFF 90%)',
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default BusinessDashboard; 